// Photos for the Play screen cards, taken in the real game: tracks (helicopter shot), atmospheres (chase
// camera), item effects (staged by the roadside) and arcade games (a frame of a simulated play).
// One photo at a time, when the browser is idle, then kept in IndexedDB: redone only if what it shows changes.
import type { TrackDef } from '@gruvetto/core';
import { gameShot } from '../../games/host.js';
import { db } from '../../data/db.js';
import { state } from '../../data/store.js';
import { catalog, themeContent } from '../../data/creations.js';
import type { ItemStage } from './itemStage.js';
import { softwareRendering } from '../../render/gpu.js';
import { photosWanted } from './photoPolicy.js';

const W = 1280, H = 600, VERSION = 9, KEEP = 80;
/** Shot formats: cards, and wide banners (event page hero) that stay sharp across the page width. */
export const SIZES = { card: [W, H], wide: [1920, 720], share: [1440, 960] } as const;
export type PhotoSize = keyof typeof SIZES;
export interface PhotoJob { key: string; run: () => Promise<Blob> }
interface Queued extends PhotoJob { root: HTMLElement }

const urls = new Map<string, string>();
const failed = new Set<string>(), tries = new Map<string, number>();
let queue: Queued[] = [];
let running = false, release: ReturnType<typeof setTimeout> | undefined;

function hash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(36);
}
/** Cache key: everything visible in the photo, plus the staging version. */
const keyOf = (...parts: unknown[]) => 'p' + hash(JSON.stringify([VERSION, ...parts]));
/** Cache key for photos taken outside this module (rider portrait). */
export const photoKey = keyOf;
const rider = () => [state.profile.color, state.profile.look];

/** The 3D studio (three.js, the renderer): fetched the first time a photo has to be taken, never for cached ones. */
type Studio = typeof import('./photoStudio.js');
let studioMod: Studio | null = null;
const studio = (): Promise<Studio> => (studioMod ? Promise.resolve(studioMod) : import('./photoStudio.js').then(m => (studioMod = m)));

// toBlob copies the image right away: the WebGL buffer of the frame just drawn is still there
const blobOf = (c: HTMLCanvasElement) => new Promise<Blob>((res, rej) => c.toBlob(b => (b ? res(b) : rej(new Error('toBlob'))), 'image/jpeg', 0.85));

/** Staging of track photos: bumped when the spot or the framing changes (world2: never inside a tunnel). */
const TRACK_SHOT = 'world2';

/** Track seen from a helicopter above its steepest climb (or its tightest bend), outside any tunnel. */
export function trackPhoto(def: TrackDef, themeId: string, size: PhotoSize = 'card'): PhotoJob {
  return {
    // the card format keeps its historical key: photos already cached stay valid
    key: size === 'card' ? keyOf('track', def.pts, themeContent(themeId), rider(), catalog.get(def.id ?? '', 'track')?.creation.content.world ?? null, TRACK_SHOT) : keyOf('track', def.pts, themeContent(themeId), rider(), size, catalog.get(def.id ?? '', 'track')?.creation.content.world ?? null, TRACK_SHOT),
    run: async () => (await studio()).shootTrack(def, themeId, size),
  };
}

/** The two large frames of the share images (a click asks for them: no queue, no cache). Frees the 3D context afterwards. */
export async function sharePhotos(def: TrackDef, themeId: string): Promise<{ aerial: Blob; chase: Blob }> {
  const s = await studio();
  try { return { aerial: await s.shootTrack(def, themeId, 'share'), chase: await s.shootRider(def, themeId) }; } finally { s.release(); }
}

/** Atmosphere seen from the chase camera, on the reference track. */
export function themePhoto(themeId: string): PhotoJob {
  return { key: keyOf('theme', themeContent(themeId), rider()), run: async () => (await studio()).shootTheme(themeId) };
}

/** Effect of an item staged on the reference track, in the chosen atmosphere. */
export function itemPhoto(id: string, st: ItemStage, themeId: string): PhotoJob {
  return { key: keyOf('item', id, st, themeContent(themeId), rider()), run: async () => (await studio()).shootItem(st, themeId) };
}

/** Frame of an arcade game after a few seconds of simulated play. */
export function gamePhoto(id: string, code: string): PhotoJob {
  // games draw in 16:9: the photo keeps that format, without black bars
  return { key: keyOf('game', id, hash(code)), run: async () => blobOf(await gameShot(code, { width: 640, height: 360, seconds: 20, timeout: 15000 })) };
}

// ---------- display and queue ----------
/** Slot of a photo in a card; `overlay` (pictogram) stays visible until the photo is ready. */
export function photoSlot(key: string, overlay = ''): string {
  const url = urls.get(key);
  return `<span class="photo${url ? ' ready' : ''}" data-photo="${key}">${url ? `<img src="${url}" alt="">` : ''}${overlay}</span>`;
}

function show(key: string) {
  const url = urls.get(key);
  if (!url) return;
  for (const slot of document.querySelectorAll<HTMLElement>(`[data-photo="${key}"]`)) {
    // the overlay may hold its own image (fallback swatch): only the photo is a direct child
    if (slot.querySelector(':scope > img')) continue;
    const img = document.createElement('img');
    img.alt = '';
    img.onload = () => slot.classList.add('ready');
    img.src = url;
    slot.prepend(img);
  }
}

const alive = (root: HTMLElement) => root.isConnected && root.getClientRects().length > 0;

/** Fills the slots of `root`, in the given order. A new request for the same `root` replaces the previous one. */
export function loadPhotos(root: HTMLElement, jobs: PhotoJob[]) {
  queue = queue.filter(q => q.root !== root && alive(q.root));
  for (const j of jobs) {
    if (urls.has(j.key)) { show(j.key); continue; }
    if (failed.has(j.key) || queue.some(q => q.key === j.key)) continue;
    queue.push({ ...j, root });
  }
  void run();
}

async function run() {
  if (running) return;
  running = true;
  clearTimeout(release);
  try {
    while (queue.length) {
      const job = queue.shift()!;
      if (!alive(job.root) || urls.has(job.key)) continue;
      const saved = (await db.get('photos', job.key).catch(() => null)) as { blob: Blob } | null;
      if (saved?.blob) { urls.set(job.key, URL.createObjectURL(saved.blob)); show(job.key); continue; }
      // photos already taken are still shown; new ones only when this machine can afford them (setting "Card photos").
      // Not marked as failed: switching the setting on takes them at the next visit.
      if (!photosWanted(state.settings.photos, softwareRendering())) continue;
      // building a world blocks the main thread for a few hundred milliseconds: wait for a quiet moment
      await new Promise<void>(res => (typeof requestIdleCallback === 'function' ? requestIdleCallback(() => res(), { timeout: 600 }) : setTimeout(res, 60)));
      if (!alive(job.root)) continue;
      try {
        // User Timing entry: the cost of each photo is visible in DevTools and measured by the e2e journey
        const t0 = performance.now();
        const blob = await job.run();
        performance.measure('gv-photo', { start: t0, detail: job.key });
        urls.set(job.key, URL.createObjectURL(blob));
        show(job.key);
        await store(job.key, blob);
      } catch (e) {
        // a game is slow when the machine is busy: a second chance at the end of the queue; after that (no WebGL,
        // crashing game) the card keeps its pictogram
        const n = (tries.get(job.key) ?? 0) + 1;
        tries.set(job.key, n);
        if (n < 2) queue.push(job);
        else { failed.add(job.key); console.warn('Photo impossible', e); }
      }
    }
  } finally {
    running = false;
    // the off-screen view holds a WebGL context: release it when nothing is requested any more
    release = setTimeout(() => { if (!running) studioMod?.release(); }, 4000);
  }
}

async function store(key: string, blob: Blob) {
  try {
    await db.put('photos', { key, blob, at: Date.now() });
    const all = ((await db.all('photos')) as { key: string; at: number }[]).sort((a, b) => b.at - a.at);
    for (const old of all.slice(KEEP)) await db.del('photos', old.key);
  } catch { /* the cache is a convenience: the photo stays displayed for this session */ }
}
